import dayjs from "dayjs";
import { createScopedLogger } from "../../../common";
import { IExpressionEvaluatorContext } from "../../../ExpressionsModule";
import { IExpression } from "../../../ExpressionsModule/Models/IExpression";
import { VariableNames } from "../../Constants/VariableNames";

const logger = createScopedLogger("DataDisplayComponent");

/**
 * Extracts the key value from a selected lookup item
 */
export function extractLookupKeyValue(editValue: any, keyField: string = "Id"): any {
  if (!editValue || dayjs.isDayjs(editValue)) {
    return null;
  }

  if (typeof editValue === "object") {
    return editValue[keyField];
  }

  return null;
}

/**
 * Handles saving of a lookup field by updating Item, Value, and calling ItemSelected
 */
export async function handleLookupFieldSave(
  editValue: any,
  lookupConfig: {
    ItemKeyField?: string;
    ItemSelected?: IExpression;
    Item?: IExpression;
    Value?: IExpression;
  },
  context: IExpressionEvaluatorContext,
  eventContext: IExpressionEvaluatorContext
): Promise<any> {
  const keyField = lookupConfig.ItemKeyField || "Id";
  const keyValue = extractLookupKeyValue(editValue, keyField);

  // If ItemSelected is defined, always call it (even with null to handle clearing)
  if (lookupConfig.ItemSelected) {
    eventContext.VariableScope.set(VariableNames.Event, { value: keyValue });
    await eventContext.Evaluator.evaluate(lookupConfig.ItemSelected, eventContext);
  }

  // Try to update Item with the full selected object (will fail gracefully if not assignable)
  if (lookupConfig.Item && editValue) {
    const itemAssigned = context.Evaluator.tryAssign(lookupConfig.Item, context, editValue);
    if (itemAssigned) {
      logger.debug("Successfully assigned selected lookup item to Item", editValue);
    }
  }

  // Try to update Value with the key field (will fail gracefully if not assignable)
  if (lookupConfig.Value && keyValue !== null) {
    const valueAssigned = context.Evaluator.tryAssign(lookupConfig.Value, context, keyValue);
    if (valueAssigned) {
      logger.debug("Successfully assigned selected lookup value to Value", keyValue);
    }
  }

  // For lookup fields, return the selected item as the value to send
  return editValue;
}

/**
 * Updates the context with the new value and refreshes data
 */
export async function updateContextAndRefresh(
  updateDataExpression: IExpression | undefined,
  valueToSend: any,
  getDataExpression: IExpression,
  fieldPath: string | undefined,
  isLookupField: boolean,
  context: IExpressionEvaluatorContext,
  eventContext: IExpressionEvaluatorContext,
  dataRefresh: () => Promise<void>
): Promise<void> {
  // Only call UpdateData if it's defined
  if (updateDataExpression) {
    eventContext.VariableScope.set(VariableNames.Event, { value: valueToSend });
    await eventContext.Evaluator.evaluate(updateDataExpression, eventContext);

    // Update variables in the context variable scope
    if (fieldPath && !isLookupField) {
      context.Evaluator.assign(getDataExpression, context, valueToSend, fieldPath.split("."));
    }
  }

  // Refresh data after update
  await dataRefresh();
}
